/**
 * TableOfContents Component
 *
 * Sticky sidebar TOC for long-form content (desktop).
 * Uses IntersectionObserver to highlight the current section.
 *
 * Also exports TableOfContentsMobile for mobile/tablet (collapsible <details>).
 *
 * Usage:
 *   // Desktop (sticky sidebar)
 *   <TableOfContents headings={headings} />
 *
 *   // Mobile (collapsible)
 *   <TableOfContentsMobile headings={headings} />
 */
import { enhanceAnchorClick } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { TocHeading } from '@/shared/lib/types/content';
import { plaintextString, type LocalizedText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { useEffect, useRef, useState } from 'react';

export type { TocHeading };

interface TableOfContentsProps {
  headings: TocHeading[];
  /** Title shown above the TOC — required so no hardcoded English default leaks. */
  title: LocalizedText;
}

/**
 * Desktop TOC - Sticky sidebar with active section highlighting
 */
export function TableOfContents({ headings, title }: TableOfContentsProps) {
  const tA11y = useTranslations('common');
  // Initialize with first heading to avoid setState in effect
  const [activeId, setActiveId] = useState<string | null>(headings[0]?.anchorId ?? null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (headings.length === 0) return;

    // Create observer with center-band detection
    // Root margin: -40% top, -50% bottom = only middle ~10% triggers
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Find the first intersecting entry
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            return;
          }
        }
      },
      {
        rootMargin: '-40% 0px -50% 0px',
        threshold: 0.01,
      }
    );

    // Observe all heading elements
    headings.forEach((heading) => {
      const element = document.getElementById(heading.anchorId);
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [headings]);

  // The enhancement over the fragment anchor: a smooth scroll in place of the
  // browser's jump, and the hash written without one. The anchor stays the
  // contract — the seam's `enhanceAnchorClick` takes only a plain primary
  // click, so cmd-click still opens the section in a new tab and a reader
  // with no JS still jumps to it (LET-83; it used to `preventDefault()`
  // unconditionally, which swallowed every modifier).
  const jumpTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Update URL hash without jump
      window.history.pushState(null, '', `#${id}`);
      setActiveId(id);
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <nav aria-label={tA11y('a11y.tableOfContents')} className="text-sm">
      <h2 className="text-base font-medium mb-3 text-foreground">{title}</h2>
      <ul className="space-y-1">
        {headings.map((heading) => {
          const isActive = activeId === heading.anchorId;
          const isSubheading = heading.level >= 3;

          return (
            <li
              key={heading.anchorId}
              className={cn(
                'border-l-2 py-1 transition-colors',
                isActive ? 'border-primary' : 'border-transparent',
                isSubheading && 'ml-3'
              )}
            >
              <a
                href={`#${heading.anchorId}`}
                onClick={enhanceAnchorClick({ onActivate: () => jumpTo(heading.anchorId) })}
                className={cn(
                  'block pl-3 transition-colors hover:text-foreground',
                  isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
                )}
                aria-current={isActive ? 'true' : undefined}
              >
                {plaintextString(heading.text)}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Mobile TOC - Collapsible <details> element for mobile/tablet
 * Native HTML, no JS required, accessible by default
 */
export function TableOfContentsMobile({ headings, title }: TableOfContentsProps) {
  const tA11y = useTranslations('common');
  if (headings.length === 0) {
    return null;
  }

  return (
    <details className="mb-8 rounded-lg border border-border bg-muted/30">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50">
        {title}
      </summary>
      <nav aria-label={tA11y('a11y.tableOfContents')} className="px-4 pb-4 pt-2">
        <ul className="space-y-1 text-sm">
          {headings.map((heading) => {
            const isSubheading = heading.level >= 3;

            return (
              <li key={heading.anchorId} className={cn('py-1', isSubheading && 'ml-4')}>
                <a
                  href={`#${heading.anchorId}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {plaintextString(heading.text)}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </details>
  );
}
